import { benchmarkById } from "~/lib/siteglue/agent-readiness/benchmarks";
import {
  CONVERSION_FLOW_CHECK_IDS,
  FLOW_TITLES,
} from "~/lib/siteglue/agent-readiness/conversion-flow";
import { CRO_BENCHMARKS, type CroBenchmarkId } from "~/lib/siteglue/agent-readiness/cro-v5";
import { AGENT_READINESS_CHECKS } from "~/lib/siteglue/agent-readiness/registry";
import {
  AEO_PLANNED_VERSION,
  AEO_VERSION,
  AGENT_READINESS_VERSION,
  CRO_V5_VERSION,
} from "~/lib/siteglue/agent-readiness/types";
import {
  AUDIT_AEO_HREF,
  AUDIT_ARO_HREF,
  AUDIT_CRO_HREF,
  BENCHMARK_LIBRARY_HREF,
} from "~/lib/siteglue/landing-redesign/copy";

export type OptimizationService = "aro" | "cro" | "aeo";
export type BenchmarkStatus = "operational" | "planned";

export type BenchmarkFaq = { q: string; a: string };

export type BenchmarkDefinition = {
  id: string;
  slug: string;
  name: string;
  service: OptimizationService;
  version: string;
  status: BenchmarkStatus;
  category: string;
  what: string;
  why: string;
  howEvaluated: string;
  passCriteria: string;
  failCriteria: string;
  notVerifiedCriteria: string;
  notApplicableCriteria: string;
  scoringWeight: number;
  evidenceRequirements: string;
  examplePass: string;
  exampleFail: string;
  recommendedFixes: string[];
  relatedIds: string[];
  faqs: BenchmarkFaq[];
  auditHref: string;
};

export function benchmarkSlugFromId(id: string): string {
  return id
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function benchmarkLibraryHref(slug: string): string {
  return `${BENCHMARK_LIBRARY_HREF}/${encodeURIComponent(slug)}`;
}

function detectionLabel(detection: string): string {
  if (detection === "http") return "HTTP request to a well-known or published URL";
  if (detection === "html") return "HTML and structured markup on the public page";
  if (detection === "dns") return "DNS lookup";
  if (detection === "browser") return "Browser or interactive probe when one actually ran";
  return detection;
}

function relatedFrom(ids: string[], self: string): string[] {
  return ids.filter((id) => id !== self);
}

function aroPages(): BenchmarkDefinition[] {
  const byCategory = new Map<string, string[]>();
  for (const check of AGENT_READINESS_CHECKS) {
    const list = byCategory.get(check.category) ?? [];
    list.push(check.id);
    byCategory.set(check.category, list);
  }
  return AGENT_READINESS_CHECKS.map((check) => {
    const meta = benchmarkById(check.id);
    const weight = check.weight;
    return {
      id: check.id,
      slug: benchmarkSlugFromId(check.id),
      name: check.title,
      service: "aro" as const,
      version: AGENT_READINESS_VERSION,
      status: "operational" as const,
      category: check.category,
      what: `${check.title} is an Agent Readiness Optimization check in the ${check.category} group.`,
      why: check.impact,
      howEvaluated: `${detectionLabel(check.detection)}. Spec: ${check.specUrl}. Catalog ${AGENT_READINESS_VERSION}.`,
      passCriteria: `The published signal for ${check.title} is present and readable.`,
      failCriteria: `The scan requested the signal and it was missing, blocked, or malformed.`,
      notVerifiedCriteria:
        "The check was not selected, the probe did not run, or evidence was inconclusive. Not verified is never scored as 0.",
      notApplicableCriteria:
        weight === 0
          ? "Optional checks can be recorded without changing the ARO score."
          : "Not applicable when the business has no corresponding surface (for example commerce protocols on a site with no cart).",
      scoringWeight: weight,
      evidenceRequirements: `Public HTTP, HTML, or DNS evidence for ${check.title}. No private credentials.`,
      examplePass: `Agents can read a valid ${check.title} signal on the public site.`,
      exampleFail: `${check.title} is missing, blocked, or not machine-readable.`,
      recommendedFixes: meta?.instructions ? [meta.instructions] : ["Use the Improve rail on the ARO report for this check."],
      relatedIds: relatedFrom(byCategory.get(check.category) ?? [], check.id),
      faqs: [
        {
          q: "Does a missing optional check lower my ARO score?",
          a: weight === 0 ? "No. Weight 0 checks are recorded and shown. They do not change the score." : "Yes, if the check is applicable and failed. Not verified items are excluded.",
        },
        {
          q: "Is this a live ChatGPT test?",
          a: "No. ARO is a website audit. Optional live walks are Test With AI Agents.",
        },
      ],
      auditHref: AUDIT_ARO_HREF,
    };
  });
}

const CRO_RELATED: Record<CroBenchmarkId, string[]> = {
  first_impression: ["cta", "trust"],
  cta: ["first_impression", "conversion_flow"],
  trust: ["first_impression", "mobile"],
  mobile: ["cta", "conversion_flow"],
  conversion_flow: [...CONVERSION_FLOW_CHECK_IDS],
};

function croBenchmarkPages(): BenchmarkDefinition[] {
  return CRO_BENCHMARKS.map((item) => ({
    id: item.id,
    slug: benchmarkSlugFromId(item.id),
    name: item.label,
    service: "cro" as const,
    version: CRO_V5_VERSION,
    status: "operational" as const,
    category: "CRO",
    what: item.question,
    why: item.description,
    howEvaluated: `CRO ${CRO_V5_VERSION}. Equal mean of eligible benchmarks (the 20% model). A benchmark scores only with coverage of at least 50% and at least two conclusive checks.`,
    passCriteria: "Enough conclusive checks pass for this benchmark on the public pages or probed channel.",
    failCriteria: "Conclusive checks fail: the visitor cannot complete this part of the journey.",
    notVerifiedCriteria:
      "Not verified is excluded, never scored as 0. Conversion Flow later steps stay not verified until they are actually tested.",
    notApplicableCriteria: "Not applicable when the surface does not exist for this business.",
    scoringWeight: 1,
    evidenceRequirements: "Public page HTML, channel findings, or an interactive probe that actually ran.",
    examplePass: item.id === "conversion_flow"
      ? "A visitor can go from entry through confirmation on a tested path (for example Visitor → AI Agent → Intent → Booking → Confirmation)."
      : `The public site shows clear evidence for ${item.label}.`,
    exampleFail: item.id === "conversion_flow"
      ? "A phone number, chat launcher, or enabled SiteGlue channel is treated as a finished conversion journey."
      : `${item.label} is missing or not usable.`,
    recommendedFixes: ["Use the Improve rail on the CRO report.", "Close dead ends between intent and confirmation."],
    relatedIds: CRO_RELATED[item.id],
    faqs: [
      {
        q: "Does turning on a SiteGlue channel pass this benchmark?",
        a: "No. Enabling a channel is not a Conversion Flow pass. Untested steps stay not verified.",
      },
    ],
    auditHref: AUDIT_CRO_HREF,
  }));
}

function conversionFlowPages(): BenchmarkDefinition[] {
  return CONVERSION_FLOW_CHECK_IDS.map((id) => ({
    id,
    slug: benchmarkSlugFromId(id),
    name: FLOW_TITLES[id],
    service: "cro" as const,
    version: CRO_V5_VERSION,
    status: "operational" as const,
    category: "Conversion Flow",
    what: `${FLOW_TITLES[id]} is a Conversion Flow check inside CRO, not a separate public score.`,
    why: "Visitors should complete a request without losing context or hitting a dead end. Entry alone is not a completed journey.",
    howEvaluated: `CRO ${CRO_V5_VERSION}. Later steps stay not verified until a probe or walk actually tests them.`,
    passCriteria: "The step was tested and completed.",
    failCriteria: "The step was tested and broke, dead-ended, or dropped context.",
    notVerifiedCriteria: "The step was not tested on this scan. Not verified is never a fake 0.",
    notApplicableCriteria: "Not applicable when the business has no corresponding action (for example no booking).",
    scoringWeight: 1,
    evidenceRequirements: "An interactive probe, owned SiteGlue channel test, or labeled agent walk that actually ran.",
    examplePass: `${FLOW_TITLES[id]} completed on a tested path.`,
    exampleFail: `${FLOW_TITLES[id]} was assumed from a published phone number or chat launcher.`,
    recommendedFixes: ["Test a later step past entry.", "Offer human handoff and confirmation."],
    relatedIds: ["conversion_flow", ...relatedFrom([...CONVERSION_FLOW_CHECK_IDS], id)],
    faqs: [
      {
        q: "Is this its own CRO score?",
        a: "No. Conversion Flow is one of five CRO benchmarks. These checks feed that benchmark.",
      },
    ],
    auditHref: AUDIT_CRO_HREF,
  }));
}

export const AEO_BENCHMARKS: Omit<BenchmarkDefinition, "relatedIds">[] = [
  {
    id: "aeo_answer_visibility",
    slug: "aeo-answer-visibility",
    name: "AI answer visibility",
    service: "aeo",
    version: AEO_PLANNED_VERSION,
    status: "planned",
    category: "Visibility",
    what: "Whether named answer engines actually surface this business for relevant questions.",
    why: "Being technically crawlable is not the same as being recommended in an answer engine.",
    howEvaluated: "Requires a live labeled test against a named engine. Until that test runs, the result is Not verified.",
    passCriteria: "A live labeled test shows the business in the answer for a documented query.",
    failCriteria: "A live labeled test ran and the business was absent from the answer.",
    notVerifiedCriteria: "No live ChatGPT, Gemini, Perplexity, or other engine test ran. SiteGlue does not invent a recommendation.",
    notApplicableCriteria: "Not applicable when the business asks not to be queried in public engines.",
    scoringWeight: 1,
    evidenceRequirements: "Query, engine name, timestamp, and quoted answer. Simulations are labeled simulations.",
    examplePass: "A live labeled ChatGPT test cites the business for a documented local-service query.",
    exampleFail: "A live labeled test ran and the engine named a competitor instead.",
    recommendedFixes: ["Publish clear entity and service pages.", "Do not treat an ARO pass as AEO visibility."],
    faqs: [
      {
        q: "Does SiteGlue claim ChatGPT recommends my business?",
        a: "Only if a live labeled test on this report says so. Otherwise this check stays Not verified.",
      },
    ],
    auditHref: AUDIT_AEO_HREF,
  },
  {
    id: "aeo_brand_mentions",
    slug: "aeo-brand-mentions",
    name: "Brand mentions",
    service: "aeo",
    version: AEO_PLANNED_VERSION,
    status: "planned",
    category: "Visibility",
    what: "Whether answer engines mention the brand name in relevant answers.",
    why: "Mentions without a citation still affect whether people hear about the business.",
    howEvaluated: "Live labeled engine tests. Planned until those tests run.",
    passCriteria: "A live test quotes or names the brand in the answer.",
    failCriteria: "A live test ran and the brand was not mentioned.",
    notVerifiedCriteria: "No live mention test ran.",
    notApplicableCriteria: "Not applicable for brands that request no public engine queries.",
    scoringWeight: 1,
    evidenceRequirements: "Engine, query, and quoted mention.",
    examplePass: "The engine names the business in the answer body.",
    exampleFail: "The engine answers the topic and never names the business.",
    recommendedFixes: ["Use a consistent legal and public name in structured data and llms.txt."],
    faqs: [{ q: "Is a Directory score a brand mention?", a: "No. Directory AEO stays Not yet verified until this test exists." }],
    auditHref: AUDIT_AEO_HREF,
  },
  {
    id: "aeo_citation_quality",
    slug: "aeo-citation-quality",
    name: "Citation quality",
    service: "aeo",
    version: AEO_PLANNED_VERSION,
    status: "planned",
    category: "Citations",
    what: "Whether citations point at the business site rather than unrelated third parties.",
    why: "A mention with a wrong URL still sends people elsewhere.",
    howEvaluated: "Live labeled engine tests that capture cited URLs.",
    passCriteria: "Citations include the business domain for the documented query.",
    failCriteria: "Citations omit the business or point at the wrong property.",
    notVerifiedCriteria: "No live citation capture ran.",
    notApplicableCriteria: "Not applicable when the engine provides no citations.",
    scoringWeight: 1,
    evidenceRequirements: "Cited URLs from a live labeled test.",
    examplePass: "The answer cites the business homepage or a relevant service page.",
    exampleFail: "The answer cites a directory or competitor instead of the business site.",
    recommendedFixes: ["Publish stable service URLs and Organization markup."],
    faqs: [{ q: "Does an MCP card count as a citation?", a: "No. MCP is ARO. Citations are answer-engine evidence." }],
    auditHref: AUDIT_AEO_HREF,
  },
  {
    id: "aeo_entity_content_clarity",
    slug: "aeo-entity-content-clarity",
    name: "Entity and content clarity",
    service: "aeo",
    version: AEO_VERSION,
    status: "operational",
    category: "Content",
    what: "Whether the public site names the business, services, location, and contact in machine-readable form.",
    why: "Answer engines need a clear entity. This is technical AEO readiness, not a claim that ChatGPT recommends you.",
    howEvaluated: "HTML and JSON-LD on the scanned pages. Same public facts ARO uses for business knowledge.",
    passCriteria: "Organization identity plus services or contact are present in structured or clearly marked content.",
    failCriteria: "The site does not identify the business or what it offers in a machine-readable way.",
    notVerifiedCriteria: "Pages could not be fetched.",
    notApplicableCriteria: "Rare. Most public businesses should identify themselves.",
    scoringWeight: 1,
    evidenceRequirements: "JSON-LD Organization/Service/Contact or equivalent visible main content.",
    examplePass: "JSON-LD names the organization, a service, and a contact point.",
    exampleFail: "The homepage is a JavaScript shell with no organization or service markup.",
    recommendedFixes: ["Add Organization and Service JSON-LD.", "Publish llms.txt with the same public facts."],
    faqs: [
      {
        q: "If this passes, am I recommended by ChatGPT?",
        a: "No. This is technical clarity on your website. Answer visibility stays Not verified without a live labeled engine test.",
      },
    ],
    auditHref: AUDIT_AEO_HREF,
  },
  {
    id: "aeo_share_of_voice",
    slug: "aeo-share-of-voice",
    name: "Competitive share of voice",
    service: "aeo",
    version: AEO_PLANNED_VERSION,
    status: "planned",
    category: "Competition",
    what: "How often this business appears versus named competitors in documented answer-engine queries.",
    why: "Visibility is relative. A mention can still lose to a competitor.",
    howEvaluated: "Live labeled tests across a documented query set. Planned until those tests run.",
    passCriteria: "The business appears at least as often as documented competitors on the query set.",
    failCriteria: "Live tests ran and competitors dominate the answers.",
    notVerifiedCriteria: "No competitive query set was tested live.",
    notApplicableCriteria: "Not applicable when the business has no comparable competitors.",
    scoringWeight: 1,
    evidenceRequirements: "Query set, competitor list, and live engine answers.",
    examplePass: "The business is cited in a majority of documented category queries.",
    exampleFail: "Competitors are cited and the business is absent.",
    recommendedFixes: ["Clarify category, service area, and differentiators on public pages."],
    faqs: [{ q: "Do you scrape ChatGPT to estimate share of voice?", a: "Not without a live labeled test recorded on the report." }],
    auditHref: AUDIT_AEO_HREF,
  },
  {
    id: "aeo_answer_accuracy",
    slug: "aeo-answer-accuracy",
    name: "Answer accuracy",
    service: "aeo",
    version: AEO_PLANNED_VERSION,
    status: "planned",
    category: "Accuracy",
    what: "Whether answer engines state hours, location, and services that match the business's public facts.",
    why: "A recommendation with the wrong hours still fails the caller.",
    howEvaluated: "Compare live engine answers to the scanned public facts. Planned until live tests run.",
    passCriteria: "Live answers match published hours, location, and services.",
    failCriteria: "Live answers contradict the public site.",
    notVerifiedCriteria: "No live accuracy comparison ran.",
    notApplicableCriteria: "Not applicable when the business has no stable public facts.",
    scoringWeight: 1,
    evidenceRequirements: "Quoted engine answer plus the matching site fact.",
    examplePass: "The engine reports the same hours published on the site.",
    exampleFail: "The engine reports a closed location or outdated phone number.",
    recommendedFixes: ["Keep NAP, hours, and services consistent across pages and JSON-LD."],
    faqs: [{ q: "Can ARO structured data fix accuracy by itself?", a: "It helps engines read facts. Accuracy still needs a live answer to compare." }],
    auditHref: AUDIT_AEO_HREF,
  },
];

function aeoPages(): BenchmarkDefinition[] {
  const ids = AEO_BENCHMARKS.map((item) => item.id);
  return AEO_BENCHMARKS.map((item) => ({
    ...item,
    relatedIds: relatedFrom(ids, item.id),
  }));
}

export const BENCHMARK_DEFINITIONS: BenchmarkDefinition[] = [
  ...aroPages(),
  ...croBenchmarkPages(),
  ...conversionFlowPages(),
  ...aeoPages(),
];

export function benchmarkBySlug(slug: string): BenchmarkDefinition | undefined {
  return BENCHMARK_DEFINITIONS.find((item) => item.slug === slug);
}

export function benchmarksForService(service: OptimizationService): BenchmarkDefinition[] {
  return BENCHMARK_DEFINITIONS.filter((item) => item.service === service);
}

export function filterBenchmarkLibrary(input: {
  service?: OptimizationService | "all";
  query?: string;
}): BenchmarkDefinition[] {
  const service = input.service && input.service !== "all" ? input.service : null;
  const query = input.query?.trim().toLowerCase() ?? "";
  return BENCHMARK_DEFINITIONS.filter((item) => {
    if (service && item.service !== service) return false;
    if (!query) return true;
    const haystack = `${item.name} ${item.id} ${item.what} ${item.category} ${item.service}`.toLowerCase();
    return haystack.includes(query);
  });
}
